import React from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import Tooltip from 'components/Tooltip'

import Apptentive from 'helpers/apptentive'
import GA4 from 'helpers/ga4'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

export type StatusButtonProps = {
  text: string
  quantity: number | string | undefined
  icon?: React.ReactNode
  img?: string
  imgAlt?: string
  accentColor: string
  applyAccentColorToQuantity?: boolean
  hasStripedAccentColor?: boolean
  stripSize?: string[]
  apptentiveEventName: string
  gaEventName?: string
  filter: string
  tooltip: string
  className?: string
  textStyle?: string
}

const StatusButton = ({ text, quantity = 0, icon, img, imgAlt, accentColor, applyAccentColorToQuantity = false, hasStripedAccentColor = false, stripSize = ['10px', '20px'], apptentiveEventName, gaEventName, filter, tooltip, className, textStyle }: StatusButtonProps) => {
  const navigate = useNavigate()

  const onClick = () => {
    Apptentive.sendEvent(apptentiveEventName)

    GA4.sendEvent({
      category: 'Dashboard Page',
      action: 'Click Status Button',
      label: gaEventName || text
    })

    navigate(`/customers?filters=${filter}`)
  }

  return (
    <Tooltip title={tooltip}>
      <S.Button {...{ className, onClick }}>
        <S.AccentColor isStriped={hasStripedAccentColor} color={accentColor} stripSize={stripSize} />

        <S.Contents>
          {icon && <S.IconWrapper>{icon}</S.IconWrapper>}
          {img && <S.Img src={img} alt={imgAlt ?? `${text} icon`} />}
          <S.Text $textStyle={textStyle}>{text}</S.Text>
          <S.Quantity applyAccentColor={applyAccentColorToQuantity} accentColor={accentColor}>{quantity}</S.Quantity>
        </S.Contents>
      </S.Button>
    </Tooltip>
  )
}

export default StatusButton

const S = {
  Button: styled.button`
    display: grid;
    grid-template-columns: 15px minmax(0, 1fr);
    padding: 0;
    justify-content: space-between;
    text-transform: capitalize;
    font-size: 15px;
    border: 1px solid ${color('border')};
    border-radius: 6px;
    color: ${color('systemStatusButtonText')};

    &:hover {
      border: 1px solid ${color('systemStatusButtonBorderHover')};
      cursor: pointer;
      box-shadow: ${color('shadowDark')} 0 2px 7px 0;
    }

    &:active {
      box-shadow: ${color('shadowDark')} inset 0 0 2px;
    }
  `,
  AccentColor: styled.div.withConfig({
    shouldForwardProp: (prop) => !['isStriped', 'color', 'stripSize'].includes(prop)
  })<{ isStriped: boolean; color: string, stripSize: string[]}>`
    height: 50px;
    width: 8px;
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;

    background: ${({ isStriped, color, stripSize }) =>
    isStriped ? `repeating-linear-gradient(
        45deg,
        ${color},
        ${color} ${stripSize[0]},
        transparent ${stripSize[0]},
        transparent ${stripSize[1]}
      )` : color
};
  `,
  Contents: styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    flex: 1;
    min-width: 0;

    @media ${screenSize.medium} {
      margin-left: 0;
      gap: 5px;
    }

    @media ${screenSize.small} {
      margin-left: 5px;
      gap: 10px;
    }
  `,
  IconWrapper: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-left: 5px;
  `,
  Img: styled.img`
    margin-left: 5px;
    width: 24px;
    height: 24px;
  `,
  Text: styled.div.withConfig({
    shouldForwardProp: (prop) => !['$textStyle'].includes(prop)
  })<{ $textStyle?: string }>`
    font-size: 12pt;
    font-family: 'Inter', regular;
    color: ${color('statusText')};
    text-align: left;
    flex: 1;
    min-width: 0;

    @media ${screenSize.medium} {
      font-size: 9pt;
    }

    @media ${screenSize.small} {
      font-size: 12pt;
    }

    ${({ $textStyle }) => $textStyle || ''}
  `,
  Quantity: styled.div.withConfig({
    shouldForwardProp: (prop) => !['applyAccentColor', 'accentColor'].includes(prop)
  })<{ applyAccentColor: boolean; accentColor: string }>`
    margin-right: 20px;
    font-weight: bold;
    font-size: 12pt;
    color: ${({ applyAccentColor, accentColor }) => (applyAccentColor ? accentColor : 'inherit')};
    flex-shrink: 0;

    @media ${screenSize.medium} {
      font-size: 9pt;
    }

    @media ${screenSize.small} {
      font-size: 12pt;
    } 
  `
}
